/**
 * ENCLOSE RECORDS — several at a time, each declared for what it is.
 *
 * The role travels with the bytes because the filename is never trusted for
 * it. What a record IS is declared by the person enclosing it, and that
 * declaration is the single signal from which the control side, the detail
 * side and every label are inferred rather than asked.
 *
 * ═══ R5.1 — WHAT THE FIRST INDEPENDENT TESTS CHANGED ═══
 *
 * 1. SEVERAL FILES, AND SEVERAL PER KIND. The control insisted on one file at
 *    a time, and a second file of the same kind quietly SUPERSEDED the first.
 *    A client with two subsidiaries sends two supplier listings; that is
 *    ordinary. Files are chosen together, each keeps its own kind, and nothing
 *    is sent until every one of them has been said.
 *
 * 2. CORRECTABLE BEFORE IT IS GOVERNED. Enclosing is a professional act and
 *    the record of it is permanent, so the place to fix a misclassification is
 *    BEFORE it happens. Each chosen file sits in a list with its kind beside
 *    it, changeable, and removable, until the accountant encloses them.
 *
 * 3. NOT LEVERI'S UNIVERSAL VOCABULARY. The four kinds are what THIS
 *    reconciliation needs, and the screen says so — "for this reconciliation"
 *    — rather than presenting them as the only kinds of record Leveri knows.
 *
 * THE KINDS ARE STILL THE SERVER'S, WORDS AND ALL. `start.kinds` carries every
 * kind, in presentation order, each with the word a person reads and the line
 * saying what it is for. The browser holds no copy of any of them — which is
 * the only way TRIAL_BALANCE never reaches an accountant.
 *
 * WHAT IS NEVER GUESSED: the kind. There is no filename rule here and there
 * must never be one. A file left to Leveri is DETERMINED on the server from
 * its content (the source agent); a file Leveri cannot settle comes back to be
 * named, with the reason.
 *
 * NOTHING IS JUDGED HERE. There is no size check and no "this looks like a
 * trial balance": the server's reader decides what it can read, and a record
 * it refuses comes back carrying the reader's own reason — which the file then
 * shows against that record, permanently, because a refusal is part of the
 * professional history of the work.
 */
import { useState } from "react";
import { EncloseRecordResponse, type RecordKind, type RecordRole, type WorkFile } from "@leveri/contracts";
import { Button, FileButton, Notice, Reading, Select } from "../../file/index.js";
import { enclose, failureMessage } from "../../api.js";

/**
 * What the file picker offers to open. It is a CONVENIENCE, not a rule: the
 * reader on the server decides what it can read, and a file it refuses comes
 * back with the reader's own reason.
 */
const ACCEPT = ".xlsx,.xls,.csv";

export interface EncloseRecordProps {
  workId: string;
  /** Every kind a record can be for this work, named by the server. */
  kinds: readonly RecordKind[];
  onEnclosed: (work: WorkFile) => void;
}

export interface Chosen {
  file: File;
  /** "" = Leveri works it out from the content (the source agent's DETERMINE step). */
  role: RecordRole | "";
  /** Present when Leveri could not tell what this file is: why, in its words. The person must name it. */
  asked?: string | undefined;
}

/** A file that must be named before sending: Leveri asked about it and it is still unnamed. */
export const needsAWord = (c: Chosen) => c.role === "" && c.asked !== undefined;
/** The word for a file left to Leveri. */
export const LEVERI_WORKS_IT_OUT = "Leveri works it out";

/**
 * THE FILES CHOSEN, EACH WITH WHAT IT IS — shared with the composer that opens
 * a piece of work, so records can be attached alongside the instruction and
 * still be DECLARED rather than guessed. One control, two places; a second
 * copy would be a second set of words for the same act.
 */
export function RecordChoices({ chosen, kinds, busy, onChange }: {
  chosen: readonly Chosen[];
  kinds: readonly RecordKind[];
  busy: boolean;
  onChange: (next: Chosen[]) => void;
}) {
  if (chosen.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-3">
      {chosen.map((held, index) => (
        <div key={`${held.file.name}-${held.file.size}`} className="grid grid-cols-1 items-start gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,280px)_auto]">
          <div className="min-w-0 md:mt-[26px]">
            <p className="truncate text-[15px] text-ink">{held.file.name}</p>
          </div>
          <Select
            label="What is this?"
            hint={held.asked && held.role === "" ? held.asked : kinds.find((kind) => kind.role === held.role)?.purpose}
            value={held.role}
            disabled={busy}
            onChange={(event) => {
              const role = event.target.value as RecordRole | "";
              onChange(chosen.map((c, i) => (i === index ? { ...c, role } : c)));
            }}
          >
            <option value="">{held.asked ? "Say what this is" : LEVERI_WORKS_IT_OUT}</option>
            {kinds.map((kind) => <option key={kind.role} value={kind.role}>{kind.label}</option>)}
          </Select>
          <button
            type="button"
            disabled={busy}
            onClick={() => onChange(chosen.filter((_c, i) => i !== index))}
            className="text-[13px] text-stone transition-colors hover:text-ink disabled:opacity-50 md:mt-[34px]"
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Files added start as "Leveri works it out": the server reads each file's
 * content and determines what it is (never its name, never its position in
 * the list). The person names a file only when Leveri asks, or to overrule it.
 */
export function withChosen(held: readonly Chosen[], files: readonly File[]): Chosen[] {
  const next = [...held];
  for (const file of files) {
    if (next.some((c) => c.file.name === file.name && c.file.size === file.size)) continue;
    next.push({ file, role: "" });
  }
  return next;
}

/** After sending: only the files Leveri could not determine stay, each with its question. */
export function askedBack(sent: readonly Chosen[], undetermined: ReadonlyArray<{ filename: string; reason: string }>): Chosen[] {
  return sent.flatMap((c) => {
    const asked = undetermined.find((u) => u.filename === c.file.name);
    return asked ? [{ file: c.file, role: "" as const, asked: asked.reason }] : [];
  });
}

export const ACCEPT_RECORDS = ACCEPT;

export function EncloseRecord({ workId, kinds, onEnclosed }: EncloseRecordProps) {
  const [chosen, setChosen] = useState<Chosen[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [refused, setRefused] = useState<Array<{ filename: string; reason: string }>>([]);

  /** Each file starts as "Leveri works it out" (see withChosen). */
  function add(files: File[]) {
    setFailure(null);
    setChosen((held) => withChosen(held, files));
  }

  const ready = chosen.length > 0 && !chosen.some(needsAWord);

  async function send() {
    if (!ready || busy) return;
    setBusy(true);
    setFailure(null);
    setRefused([]);
    try {
      const result = await enclose(
        `/api/work/${workId}/records`,
        EncloseRecordResponse,
        chosen.map((c) => c.file),
        chosen[0]!.role as string,
        chosen.map((c) => c.role as string)
      );
      setChosen(askedBack(chosen, result.undetermined));
      setRefused(result.refused);
      onEnclosed(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <RecordChoices chosen={chosen} kinds={kinds} busy={busy} onChange={setChosen} />

      <div className="flex flex-wrap items-center gap-3.5">
        <FileButton accept={ACCEPT} multiple disabled={busy} onFiles={add}>
          {chosen.length === 0 ? "Choose records" : "Add more"}
        </FileButton>
        {chosen.length > 0 && (
          <Button disabled={!ready || busy} onClick={() => void send()}>
            {chosen.length === 1 ? "Enclose this record" : `Enclose ${chosen.length} records`}
          </Button>
        )}
        {chosen.length > 0 && !ready && (
          <span className="text-[13px] text-stone">Say what each file is before enclosing it.</span>
        )}
        {chosen.length === 0 && (
          <span className="text-[13px] text-stone">
            Several at once is fine, and more than one of the same kind is fine.
          </span>
        )}
      </div>

      {busy && <div className="mt-2"><Reading>{chosen.length === 1 ? "Enclosing the record…" : "Enclosing the records…"}</Reading></div>}
      {/* A refused file is the reader's own sentence, kept beside the ones that
          were admitted — the rest of the batch is not discarded for it. */}
      {refused.map((one) => (
        <div key={one.filename} className="mt-2"><Notice>{one.filename}: {one.reason}</Notice></div>
      ))}
      {failure && <div className="mt-2"><Notice>{failure}</Notice></div>}
    </div>
  );
}
